import React, { forwardRef, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import FocusTrap from 'focus-trap-react'

import { getIsActiveTrap, setActiveTrap } from 'redux/reducers/modal'
import useKeyPress from 'hooks/useKeyPress'
import { useFocusOnElement } from 'hooks/useFocusOnElement'
import { SpecificEventMap } from 'events/lib'

import { DialogPortal } from 'components/DialogPortal/DialogPortal'

import { ReactComponent as CloseIcon } from 'images/Icon_Close.svg'

import useDrawerCloseEvent, { DrawerCloseReason } from './useDrawerCloseEvent'
import useDrawerOpenEvent from './useDrawerOpenEvent'

export { DrawerCloseReason }

export interface MyDrawerProps<T extends keyof SpecificEventMap = never> {
  onClose: (reason: DrawerCloseReason) => void
  /**
   * The container id for the dialog - must begin with 'medicare-my' to be prefixed properly
   */
  containerId: string
  children: React.ReactNode
  heading: string
  headingLevel?: '1' | '2' | '3' | '4' | '5'
  eventNamespace?: T
  openEventKey?: keyof SpecificEventMap[T]
  closeEventKey?: keyof SpecificEventMap[T]
}

const MyDrawerContent = function <T extends keyof SpecificEventMap> (
  props: Omit<MyDrawerProps<T>, 'containerId'>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const {
    onClose,
    children,
    heading,
    headingLevel = '3',
    eventNamespace,
    openEventKey,
    closeEventKey
  } = props

  const handleClose = useDrawerCloseEvent({
    ns: eventNamespace,
    elementId: closeEventKey,
    onClose
  })
  const handleOpen = useDrawerOpenEvent({
    ns: eventNamespace,
    elementId: openEventKey
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleOpen, [])
  useKeyPress('Escape', handleClose(DrawerCloseReason.EscapeKey))

  const focusOnElement = useFocusOnElement()
  const Heading = `h${headingLevel}` as const

  return (
    <div ref={ref}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        onClick={handleClose(DrawerCloseReason.BackdropClick)}
        data-testid="background"
        className="my-c-dialog-wrap ds-base"
        role="dialog"
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          onClick={(e) => {
            e.stopPropagation()
          }}
          className="my-c-drawer"
          role="dialog"
        >
          <div className="my-c-drawer__window">
            <div className="my-c-drawer__header">
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
              <Heading
                className="my-c-drawer__header-heading"
                ref={focusOnElement}
                tabIndex={0}
              >
                {heading}
              </Heading>
              <button
                onClick={handleClose(DrawerCloseReason.CloseButton)}
                type="button"
                className="ds-c-button ds-c-button--ghost my-u-p--4 my-u-ml--8"
                data-testid="close-btn"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="my-c-drawer__body">{children}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

const MyDrawerContentWithRef = forwardRef(MyDrawerContent) as <
  T extends keyof SpecificEventMap
>(
  props: Omit<MyDrawerProps<T>, 'containerId'> & {
    ref?: React.ForwardedRef<HTMLDivElement>
  }
) => ReturnType<typeof MyDrawerContent>

/**
 * @param {MyDrawerProps} props
 * @returns a drawer component in a react portal.
 * In order to have proper styling the containerId prop must start with "medicare-my"
 */
export const MyDrawer = <T extends keyof SpecificEventMap>({
  containerId,
  ...props
}: MyDrawerProps<T>) => {
  // Active trap Status's
  const dispatch = useDispatch()
  const deactivateTrap = () => {
    dispatch(setActiveTrap({ isActiveTrap: false }))
  }
  const isActiveTrap = useSelector(getIsActiveTrap)
  const [trapActive, setTrapActive] = useState(false)

  useEffect(() => setTrapActive(Boolean(isActiveTrap)), [isActiveTrap])

  return (
    <DialogPortal containerId={containerId}>
      {/* TODO: Resolve FocusTrap findDOMNode error : https://github.com/focus-trap/focus-trap-react/issues/77 */}
      <FocusTrap
        focusTrapOptions={{
          onDeactivate: deactivateTrap
        }}
        active={trapActive}
      >
        <MyDrawerContentWithRef {...props} />
      </FocusTrap>
    </DialogPortal>
  )
}
